<template>
  <div id="playlist-view">
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-info header-enhanced">
      <!-- Back Button & Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <b-button
            variant="light"
            size="sm"
            class="shadow-sm back-btn"
            title="Back to Playlists"
            @click="$router.go(-1)">
            <i class="ni ni-bold-left mr-1"></i>Back to Playlists
          </b-button>
        </b-col>
        <b-col class="text-right">
          <h3 class="text-white mb-0">
            <i class="ni ni-music-note mr-2"></i>Playlist Details
          </h3>
          <p class="text-white-50 mb-0">Manage playlist songs and settings</p>
        </b-col>
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <!-- Enhanced Playlist Info Card -->
      <b-card class="mb-4 playlist-info-card shadow-lg border-0">
        <b-row align-v="center" class="playlist-info-row">
          <!-- Playlist Thumbnail -->
          <b-col cols="12" md="3" class="text-center">
            <div class="thumbnail-container">
              <b-skeleton-wrapper :loading="thumbLoading">
                <template #loading>
                  <b-skeleton-img class="playlist-thumbnail" aspect="1:1"></b-skeleton-img>
                </template>

                <img
                  class="img-fluid rounded-lg shadow-lg playlist-thumbnail"
                  :class="playlist.thumbnail_url ? 'thumb-loaded' : 'default-thumb'"
                  :src="playlist.thumbnail_url"
                  title="Playlist Thumbnail"
                  alt="Playlist Thumbnail"
                />
              </b-skeleton-wrapper>

              <!-- Hidden elem to determine if image is loaded -->
              <img
                class="img-fluid hidden"
                :class="playlist.thumbnail_url ? 'thumb-loaded' : 'default-thumb'"
                :src="playlist.thumbnail_url"
                title="Playlist Thumbnail"
                alt="Playlist Thumbnail"
                @load="onThumbLoad"
              />
            </div>
          </b-col>

          <!-- Playlist Details -->
          <b-col cols="12" md="6">
            <div class="playlist-details">
              <h2 class="playlist-title">{{ playlist.name }}</h2>

              <div class="playlist-stats">
                <div class="stat-item">
                  <i class="ni ni-music-note text-primary"></i>
                  <span class="stat-value">{{ totalSongs }}</span>
                  <span class="stat-label">Song{{ totalSongs !== 1 ? 's' : '' }}</span>
                </div>
                <div class="stat-item">
                  <i class="ni ni-trophy" :class="premium === 'Yes' ? 'text-warning' : 'text-muted'"></i>
                  <span class="stat-value">{{ premium }}</span>
                  <span class="stat-label">Premium</span>
                </div>
                <div class="stat-item">
                  <i class="ni ni-check-bold" :class="published === 'Yes' ? 'text-success' : 'text-muted'"></i>
                  <span class="stat-value">{{ published }}</span>
                  <span class="stat-label">Published</span>
                </div>
              </div>

              <div class="playlist-actions">
                <b-button
                  variant="success"
                  size="lg"
                  class="edit-btn mr-3"
                  @click="openEditPlaylist">
                  <i class="ni ni-settings mr-2"></i>Edit Details
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="lg"
                  @click="$router.go(-1)">
                  <i class="ni ni-bold-left mr-2"></i>Back to List
                </b-button>
              </div>
            </div>
          </b-col>

          <!-- Playlist Banner -->
          <b-col cols="12" md="3" class="text-center">
            <div class="banner-container" v-if="playlist.banner_url">
              <b-skeleton-wrapper :loading="bannerLoading">
                <template #loading>
                  <b-skeleton-img class="playlist-banner"></b-skeleton-img>
                </template>

                <img
                  class="img-fluid rounded-lg shadow playlist-banner"
                  :src="playlist.banner_url"
                  title="Playlist Banner"
                  alt="Playlist Banner"
                />
              </b-skeleton-wrapper>

              <!-- Hidden elem to determine if image is loaded -->
              <img
                class="img-fluid hidden"
                :src="playlist.banner_url"
                title="Playlist Banner"
                alt="Playlist Banner"
                @load="onBannerLoad"
              />
            </div>
            <div v-else class="no-banner">
              <i class="ni ni-image text-muted"></i>
              <p class="text-muted small mt-2">No banner image</p>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <Vuetable
        data-path="songs"
        pagination-path="pagination"
        ref="vuetable"
        track-by="songId"
        :api-url="apiUrl"
        :fields="fields"
        :http-options="httpOptions"
        :css="css.table"
        :per-page="10"
        :append-params="moreParams"
        :query-params="{
          sort    : 'sort',
          page    : 'page',
          perPage : 'perPage'
        }"
        @vuetable:pagination-data="onPaginationData">

        <!-- Change Order -->
        <template
          class="text-center"
          slot="order"
          slot-scope="props">
          <select
            class="form-control"
            v-model="props.rowData.orderNo"
            @change="changeOrder(props.rowData)">
            <option
              v-for="(value, key) in orderOptions"
              :key="key"
              :value="value">
              {{ value }}
            </option>
          </select>
        </template>

        <!-- Preview -->
        <template slot="preview" slot-scope="props">
          <vue-plyr :ref="`plyr-${props.rowData.songId}`">
            <audio :id="props.rowData.songId">
              <source :src="props.rowData.url" type="audio/mp3" />
            </audio>
          </vue-plyr>
        </template>

        <!-- Remove Song -->
        <template
          class="text-center"
          slot="actions"
          slot-scope="props">
          <b-button
            variant="danger"
            @click="removeSong(props.rowData)">
            Remove
          </b-button>
        </template>
      </Vuetable>

      <!-- Enhanced Pagination -->
      <b-card class="mt-4 mb-4 shadow-sm pagination-card">
        <b-row align-v="center">
          <b-col md="6">
            <div class="pagination-info">
              <vuetable-pagination-info ref="paginationInfo" class="mb-0 text-muted"></vuetable-pagination-info>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <div class="pagination-controls">
              <vuetable-pagination
                ref="pagination"
                @vuetable-pagination:change-page="onChangePage"
                :css="css.pagination"
              ></vuetable-pagination>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Enhanced Search Section -->
      <b-card class="shadow-sm search-card border-0">
        <b-row align-v="center" class="mb-3">
          <b-col>
            <h5 class="mb-0 text-dark">
              <i class="ni ni-zoom-split-in mr-2 text-primary"></i>
              Add Songs to Playlist
            </h5>
            <small class="text-muted">Search and add new songs to this playlist</small>
          </b-col>
        </b-row>
        <Search @song-added="reloadSongList" />
      </b-card>
    </div>

    <!-- Modal -->
    <modals-container />

    <AppLoaderAdmin :isLoading="loading" />
  </div>
</template>

<script>
  import TableStyle from '../table-bootstrap-css.js';
  import baseHeader from '../../BaseHeader';

  const EditPlaylistModal = () => import('./EditPlaylistModal');
  

  export default {
    data() {
      return {
        loading    : false,
        apiUrl     : process.env.VUE_APP_ROOT_API + 'api/playlist/songs',
        moreParams : {
          plistId : this.$route.params.playlistId,
        },
        css         : TableStyle,
        httpOptions : {
          headers : {
            'Authorization' : `Bearer ${this.$store.state.auth.accessToken}`,
          },
        },
        fields : [ {
          name       : '__slot:order',
          title      : 'Order',
          titleClass : 'text-center',
          dataClass  : 'text-center',
          sortField  : 'order_no',
        }, {
          name      : 'title',
          title     : 'Title',
          sortField : 'music_title',
          direction : 'desc',
        }, {
          name       : '__slot:preview',
          title      : 'Preview',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'length',
          title      : 'Length',
          sortField  : 'length',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : '__slot:actions',
          title      : '',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        } ],
        playlist      : {},
        totalSongs    : 0,
        maxOrder      : 0,
        orderOptions  : 0,
        thumbLoading  : true,
        bannerLoading : true,
      }
    },
    computed : {
      premium() {
        return (this.playlist.premium === 1) ? 'Yes' : 'No';
      },
      published() {
        return (this.playlist.published === 1) ? 'Yes' : 'No';
      },
    },
    components : {
      'AppLoaderAdmin'     : () => import('@/components/layout/AppLoaderAdmin'),
      'Vuetable'           : () => import('vuetable-2/src/components/Vuetable'),
      'VuetablePagination' :
        () => import('vuetable-2/src/components/VuetablePagination'),
      'VuetablePaginationInfo' :
        () => import('vuetable-2/src/components/VuetablePaginationInfo'),
      'Search' : () => import('./Search'),
      baseHeader
    },
    watch : {
      'playlist.thumbnail_url'(val) {
        if (val)
          this.thumbLoading = true;
      },
      'playlist.banner_url'(val) {
        if (val)
          this.bannerLoading = true;
      },
    },
    methods : {
      
      /**
       * Table Data
       * @param data
       * @return {object}
       */
      transform(data) {
        this.orderOptions = data.result.total;
        this.totalSongs = data.result.total;

        const transformed = {};
        transformed.songs = [];

        transformed.pagination = {
          'total'         : data.result.total,
          'per_page'      : data.result.perPage,
          'current_page'  : data.result.currentPage,
          'last_page'     : data.result.lastPage,
          'next_page_url' : data.result.nextPageUrl,
          'prev_page_url' : data.result.prevPageUrl,
          'from'          : data.result.from,
          'to'            : data.result.to,
        };

        for (let i = 0; i < data.result.data.length; i++) {
          transformed.songs.push({
            id      : data.result.data[i].music_playlist_songs_id,
            orderNo : data.result.data[i].order_no,
            songId  : data.result.data[i].mobile_music_id,
            title   : data.result.data[i].music_title,
            length  : this.formatTime(data.result.data[i].length),
            url     : data.result.data[i].url,
          });

          if (data.result.data[i].order_no > this.maxOrder)
            this.maxOrder = data.result.data[i].order_no + 1;
        }

        // Trigger update audio players
        if (this.totalSongs > 0)
          this.updatePlayer();

        return transformed;
      },

      /**
       * Get Playlist Data
       */
      getPlaylist() {
        this.$http.get('api/playlist', {
          params : {
            plistId : this.$route.params.playlistId,
          },
        }).then(response => {
          this.playlist = response.data.data;
        });
      },

      /**
       * Open Edit Playlist Modal
       */
      openEditPlaylist() {
        this.$modal.show(EditPlaylistModal, {
          playlist : this.playlist,
        }, {
          height   : 'auto',
          adaptive : true,
        }, {
          'update-playlist-info' : () => {
            this.getPlaylist();
          },
        });
      },

      /**
       * Remove Song from Playlist
       * @param data
       */
      removeSong(data) {
        this.$http.delete('api/playlist/song', {
          data : {
            mPlaylistId : this.$route.params.playlistId,
            musicId     : data.songId,
          },
        }).then(() => {
          this.$store.dispatch(
            'playlist/getPlaylistSongIds',
            this.$route.params.playlistId);
          this.reloadSongList();
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success',
            text  : 'Removed song successfully!',
          });
        }).catch(() => {
          this.$notify({
            group : 'notif',
            type  : 'failed',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        });
      },

      /**
       * Update Paginaton info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$nextTick(() => {
          this.$refs.pagination.setPaginationData(paginationData);
          this.$refs.paginationInfo.setPaginationData(paginationData);
        });
      },

      /**
       * Update Vuetable page
       * @param page
       */
      onChangePage(page) {
        this.$nextTick(() => {
          if (this.$refs.vuetable.currentPage !== page)
            this.$refs.vuetable.changePage(page);
        });
      },

      /**
       * Show Loader
       */
      showLoader() {
        this.loading = true;
      },

      /**
       * Hide Loader
       */
      hideLoader() {
        this.loading = false;
      },

      /**
       * Refresh Song List
       */
      refreshSongList() {
        this.$nextTick(() => {
          this.$refs.vuetable.refresh();
        });
      },

      /**
       * Reload Song List (current page will not reset)
       */
      reloadSongList() {
        this.$nextTick(() => {
          this.$refs.vuetable.reload();
        });
      },

      /**
       * Set Alternative Thumbnail
       * @param event
       */
      setAltThumbnail(event) {
        event.target.src =
          require('../../../assets/images/music-album-placeholder.png');
      },

      /**
       * Format Song Length
       * @param value
       */
      formatTime(value) {
        let result = '';
        const hours = Math.floor(value / 3600);
        const minutes = Math.floor((value - (hours * 3600)) / 60);
        let seconds = value - (hours * 3600) - (minutes * 60);

        // round seconds & exclude remainder
        seconds = parseInt(Math.round(seconds * 100) / 100);

        if (hours > 0) {
          result = (hours < 10 ? '0' + hours : hours) +
            ':' + (minutes < 10 ? '0' + minutes : minutes) +
            ':' + (seconds < 10 ? '0' + seconds : seconds);
        } else {
          result = (minutes < 10 ? '0' + minutes : minutes) + ':' +
            (seconds < 10 ? '0' + seconds : seconds);
        }

        return result;
      },

      /**
       * Change Song Order
       * @param data
       */
      changeOrder(data) {
        this.showLoader();
        this.$http.post('api/playlist/song/order', {
          orderNo      : data.orderNo,
          mPlistSongId : data.id,
          musicId      : data.songId,
          mPlaylistId  : this.$route.params.playlistId,
          
        }).then(() => {
          this.reloadSongList();
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success!',
            text  : 'Updated song order successfully.',
          });
        }).catch(() => {
          this.$notify({
            group : 'notif',
            type  : 'error',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        }).finally(() => {
          this.hideLoader();
        });
      },

      /**
       * On Thumbnail Load
       */
      onThumbLoad() {
        this.thumbLoading = false;
      },

      /**
       * On Banner Load
       */
      onBannerLoad() {
        this.bannerLoading = false;
      },

      /**
       * Update audio player source
       */
      updatePlayer() {
        const audios = document.getElementsByTagName('audio');
        if (audios.length > 0) {
          for (let i = 0; i < audios.length; i++)
            audios[i].load();
        }
      },
    },
    mounted() {
      // Scroll to top
      window.scrollTo(0, 0);

      if (this.$route.params.playlistId)
        this.getPlaylist();

      this.$store.dispatch('playlist/getRecentSongs');

      this.$store.dispatch(
        'playlist/getPlaylistSongIds',
        this.$route.params.playlistId);

      /**
       * Audio Player Listener
       */
      document.addEventListener('playing', e => {
        const playingAudio = e.target.getElementsByTagName('audio')[0];
        const audios = document.getElementsByTagName('audio');

        if (audios.length > 0) {
          for (let i = 0; i < audios.length; i++) {
            if (audios[i] !== playingAudio)
              audios[i].pause();
          }
        }
      });
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/settings";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.9) 0%, rgba(0, 123, 191, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(0, 123, 191, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(23, 162, 184, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

.back-btn {
  transition: all 0.3s ease;
  border-radius: 8px;
}

.back-btn:hover {
  transform: translateX(-2px);
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
}

/* Enhanced Playlist Info Card */
.playlist-info-card {
  border-radius: 16px !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%);
  border: 1px solid rgba(23, 162, 184, 0.1) !important;
  animation: fadeInUp 0.8s ease-out;
}

.playlist-info-row {
  padding: 2rem;
}

.thumbnail-container {
  margin-bottom: 1rem;
}

.playlist-thumbnail {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border: 3px solid rgba(23, 162, 184, 0.2);
  transition: all 0.3s ease;
}

.playlist-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(23, 162, 184, 0.3);
}

.thumb-loaded {
  border-color: rgba(23, 162, 184, 0.4);
}

.default-thumb {
  background: linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(23, 162, 184, 0.1) 100%);
  border: 2px dashed rgba(23, 162, 184, 0.3);
}

.playlist-details {
  text-align: center;
}

.playlist-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #344767;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.playlist-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(23, 162, 184, 0.1);
  min-width: 100px;
}

.stat-item i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #344767;
}

.stat-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.playlist-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.edit-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

.banner-container {
  margin-top: 1rem;
}

.playlist-banner {
  width: 100%;
  max-width: 250px;
  height: 120px;
  object-fit: cover;
  border: 2px solid rgba(23, 162, 184, 0.2);
  transition: all 0.3s ease;
}

.playlist-banner:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 15px rgba(23, 162, 184, 0.3);
}

.no-banner {
  padding: 2rem;
  background: rgba(248, 249, 250, 0.5);
  border-radius: 8px;
  border: 2px dashed rgba(23, 162, 184, 0.3);
}

.no-banner i {
  font-size: 3rem;
}

/* Enhanced Table Styles */
table {
  z-index: 99;
  position: relative;
  background: white;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.vuetable th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px 8px !important;
}

.vuetable td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f3f4 !important;
  vertical-align: middle !important;
}

.vuetable tbody tr:hover {
  background-color: rgba(23, 162, 184, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Enhanced Form Controls */
.form-control {
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  border: 2px solid rgba(23, 162, 184, 0.2);
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

/* Enhanced Pagination */
.pagination-card {
  border-radius: 12px !important;
  border: none !important;
  background: rgba(248, 249, 250, 0.8);
}

.pagination-info {
  padding: 16px 20px;
  font-weight: 500;
  color: #6c757d;
}

.pagination-controls {
  padding: 16px 20px;
}

/* Enhanced Search Section */
.search-card {
  border-radius: 12px !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.9) 100%);
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.header {
  margin-top: -80px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .playlist-info-row {
    padding: 1rem;
  }

  .playlist-title {
    font-size: 2rem;
  }

  .playlist-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .stat-item {
    width: 100%;
    flex-direction: row;
    justify-content: center;
    text-align: left;
  }

  .playlist-actions {
    flex-direction: column;
    align-items: center;
  }

  .edit-btn,
  .btn-outline-primary {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .playlist-thumbnail {
    width: 150px;
    height: 150px;
  }

  .playlist-banner {
    height: 100px;
  }

  .vuetable tbody tr:hover {
    transform: none;
  }

  .back-btn:hover {
    transform: none;
  }

  .edit-btn:hover {
    transform: none;
  }

  .playlist-thumbnail:hover {
    transform: none;
  }

  .playlist-banner:hover {
    transform: none;
  }
}

/* Legacy styles */
.hidden {
  visibility: hidden;
  position: fixed;
  display: inline-block;
}
</style>